import { alertPropsSchema } from '@design/components/alert/Alert';
import { badgePropsSchema } from '@design/components/badge/Badge';
import { cardPropsSchema } from '@design/components/card/Card';
import { carouselPropsSchema } from '@design/components/carousel/Carousel';
import { gradientCardPropsSchema } from '@design/components/gradient-card/GradientCard';
import { gridPropsSchema } from '@design/components/grid/Grid';
import { gridRowPropsSchema } from '@design/components/grid/GridRow';
import { keyStatsPropsSchema } from '@design/components/key-stats/KeyStats';
import { markdownPropsSchema } from '@design/components/markdown';
import { orderedListPropsSchema } from '@design/components/ordered-list/OrderedList';
import { typographyPropsSchema } from '@design/components/typography/Typography';
import { priceDisplayPropsSchema } from '@design/widgets/price-display/PriceDisplay';
import {
  CHART_V2_INPUT_SERIES_TYPES,
  CHART_V2_INTERVALS,
  CHART_V2_PAYLOAD_VERSION,
  CHART_V2_RANGE_HORIZONS,
  CHART_V2_Y_AXIS_TYPES,
} from '@helpers';
import type { ReactNode } from 'react';
import { z } from 'zod';

/**
 * Generative-UI responses are intentionally a tree of named blocks.
 * The producer (today: the Alfred agent; tomorrow: any structured
 * generator) picks a `component` from a curated allow-list, and the
 * renderer maps that name to a React component via `registry.tsx`.
 *
 * This file is the runtime contract for untrusted JSON. Keep each block
 * schema JSON-serializable and intentionally narrower than the underlying
 * React component props: no callbacks, refs, React nodes, arbitrary DOM
 * attributes, or agent-controlled renderer overrides.
 */
export const GENERATIVE_UI_BLOCK_NAMES = [
  'Markdown',
  'Chart',
  'ChartV2DataProvider',
  'TimeSeriesInfo',
  'AssetOverviewHeader',
  'PriceDisplay',
  'Alert',
  'GradientCard',
  'KeyStats',
  'OrderedList',
  'SimplifiedCalendar',
  'Card',
  'Badge',
  'Accordion',
  'Table',
  'Resizable',
  'Stack',
  'Row',
  'Grid',
  'GridRow',
  'Separator',
  'Typography',
  'Carousel',
  'WatchlistTableDataProvider',
  'ScreenerTableDataProvider',
] as const;

export type GenerativeUiBlockName = (typeof GENERATIVE_UI_BLOCK_NAMES)[number];

const MAX_BLOCKS_PER_LEVEL = 100;
const MAX_TABLE_COLUMNS = 20;
const MAX_TABLE_ROWS = 500;
const MAX_CHART_SERIES = 20;
const MAX_CHART_POINTS_PER_SERIES = 5_000;
const MAX_CALENDAR_CATEGORIES = 12;
const MAX_CALENDAR_ROWS_PER_CATEGORY = 100;
const MAX_CALENDAR_COLUMNS = 4;

const chartV2HorizonSchema = z.enum([
  '1D',
  '1W',
  '2W',
  '1M',
  '2M',
  '3M',
  '6M',
  '1Y',
  '3Y',
  '5Y',
  '10Y',
  'MAX',
]);

const chartV2IntervalSchema = z.enum([
  '1m',
  '5m',
  '15m',
  '30m',
  '1h',
  '1D',
  '1W',
  '1M',
]);

const chartV2ThemeSchema = z.record(z.string(), z.string());

const chartV2LocalesSchema = z
  .object({
    searchAssets: z.string().optional(),
    clearAll: z.string().optional(),
    chartType: z.string().optional(),
    interval: z.string().optional(),
    horizon: z.string().optional(),
    noData: z.string().optional(),
    loading: z.string().optional(),
    error: z.string().optional(),
    legendMore: z.string().optional(),
    legendHide: z.string().optional(),
    legendShow: z.string().optional(),
    legendRemove: z.string().optional(),
    priceDisplayPrice: z.string().optional(),
    priceDisplayIndexed: z.string().optional(),
    contextMenuDisplaySplitYAxis: z.string().optional(),
    contextMenuDisplayMergedYAxis: z.string().optional(),
    contextMenuResetChart: z.string().optional(),
    legendMenuChangeColor: z.string().optional(),
    legendMenuMoveUp: z.string().optional(),
    legendMenuMoveDown: z.string().optional(),
    legendMenuSeriesTypeLine: z.string().optional(),
    legendMenuSeriesTypeArea: z.string().optional(),
    legendMenuSeriesTypeCandlestick: z.string().optional(),
    legendMenuSeriesTypeBar: z.string().optional(),
    zoomInToView: z.string().optional(),
  })
  .strict();

const chartV2LinePointSchema = z
  .object({
    time: z.number().finite(),
    value: z.number().finite(),
  })
  .strict();

const chartV2OhlcPointSchema = z
  .object({
    time: z.number().finite(),
    open: z.number().finite(),
    high: z.number().finite(),
    low: z.number().finite(),
    close: z.number().finite(),
  })
  .strict();

const chartV2SeriesBaseSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    yAxisLabel: z.string().optional(),
    paneId: z.string().optional(),
    showLatestPrice: z.boolean().optional(),
  })
  .strict();

const chartV2LineSeriesSchema = chartV2SeriesBaseSchema
  .extend({
    seriesType: z.enum(['line', 'area']),
    data: z.array(chartV2LinePointSchema).max(MAX_CHART_POINTS_PER_SERIES),
  })
  .strict();

const chartV2OhlcSeriesSchema = chartV2SeriesBaseSchema
  .extend({
    seriesType: z.enum(['candlestick', 'bar']),
    data: z.array(chartV2OhlcPointSchema).max(MAX_CHART_POINTS_PER_SERIES),
  })
  .strict();

export const chartBlockPropsSchema = z
  .object({
    series: z
      .array(z.union([chartV2LineSeriesSchema, chartV2OhlcSeriesSchema]))
      .min(1)
      .max(MAX_CHART_SERIES),
    horizon: chartV2HorizonSchema.optional(),
    interval: chartV2IntervalSchema.optional(),
    defaultHorizon: chartV2HorizonSchema.optional(),
    defaultInterval: chartV2IntervalSchema.optional(),
    availableHorizons: z.array(chartV2HorizonSchema).optional(),
    availableIntervals: z.array(chartV2IntervalSchema).optional(),
    disabledHorizons: z.array(chartV2HorizonSchema).optional(),
    enabledIntervals: z.array(chartV2IntervalSchema).optional(),
    theme: chartV2ThemeSchema.optional(),
    locales: chartV2LocalesSchema.optional(),
    colors: z.array(z.string()).optional(),
    showDefaultLegend: z.boolean().optional(),
    showCollapse: z.boolean().optional(),
    showMaximize: z.boolean().optional(),
  })
  .strict();

export const markdownBlockPropsSchema = markdownPropsSchema;

const isoDateStringSchema = z
  .string()
  .refine(
    value =>
      /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.+-Z]+)?$/.test(value) &&
      !Number.isNaN(Date.parse(value)),
    { message: 'Expected an ISO date string' }
  );

const chartV2DataProviderDateStringSchema = z.union([
  z.iso.date(),
  z.iso.datetime(),
]);

const chartV2DataProviderAssetSchema = z
  .object({
    tag: z.string().min(1),
    snake: z.string().min(1),
    paneIndex: z.number().int().nonnegative().optional(),
  })
  .strict();

const chartV2DataProviderDomainSchema = z.union([
  z
    .object({
      start: z.number().finite(),
      end: z.number().finite(),
    })
    .strict()
    .refine(({ start, end }) => start <= end, {
      message: 'start must be <= end',
    }),
  z
    .object({
      start: chartV2DataProviderDateStringSchema,
      end: chartV2DataProviderDateStringSchema,
    })
    .strict()
    .refine(({ start, end }) => Date.parse(start) <= Date.parse(end), {
      message: 'start must be <= end',
    }),
]);

const chartV2DataProviderLocalesSchema = z
  .object({
    empty: z.string().min(1).optional(),
    error: z.string().min(1).optional(),
  })
  .strict();

const chartV2DataProviderViewSchema = z
  .object({
    resample: z.enum(CHART_V2_INTERVALS).optional(),
    horizon: z.enum(CHART_V2_RANGE_HORIZONS).optional(),
    domain: chartV2DataProviderDomainSchema.optional(),
    primarySnake: z.string().min(1).optional(),
  })
  .strict();

const chartV2DataProviderPaneSettingsSchema = z
  .object({
    paneIndex: z.number().int().nonnegative(),
    seriesType: z.enum(CHART_V2_INPUT_SERIES_TYPES).optional(),
    yAxisType: z.enum(CHART_V2_Y_AXIS_TYPES).optional(),
  })
  .strict();

const chartV2DataProviderPayloadSchema = z
  .object({
    version: z.literal(CHART_V2_PAYLOAD_VERSION),
    assets: z.array(chartV2DataProviderAssetSchema).min(1),
    view: chartV2DataProviderViewSchema.optional(),
    panes: z.array(chartV2DataProviderPaneSettingsSchema).optional(),
    hiddenSnakes: z.array(z.string().min(1)).optional(),
    locales: chartV2DataProviderLocalesSchema.optional(),
  })
  .strict();

export const chartV2DataProviderBlockPropsSchema =
  chartV2DataProviderPayloadSchema;

export const timeSeriesInfoBlockPropsSchema = z
  .object({
    title: z.string().min(1),
    fromLabel: z.string().min(1),
    toLabel: z.string().min(1),
    from: isoDateStringSchema,
    to: isoDateStringSchema,
  })
  .strict();

export const priceDisplayBlockPropsSchema = priceDisplayPropsSchema;
export const alertBlockPropsSchema = alertPropsSchema;
export const gradientCardBlockPropsSchema = gradientCardPropsSchema;
export const keyStatsBlockPropsSchema = keyStatsPropsSchema;
export const orderedListBlockPropsSchema = orderedListPropsSchema;

export const assetOverviewHeaderBlockPropsSchema = z
  .object({
    entityTag: z.string().min(1),
    isCondensed: z.boolean().optional(),
    showShadow: z.boolean().optional(),
    showChart: z.boolean().optional(),
    locales: z
      .object({
        livePrice: z.string().min(1).optional(),
        preMarket: z.string().min(1).optional(),
        afterHours: z.string().min(1).optional(),
        asOfLastClose: z.string().min(1).optional(),
        asOfDate: z.string().min(1).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export const simplifiedCalendarBlockLocalesSchema = z
  .object({
    today: z.string().min(1).optional(),
    noEvents: z.string().min(1).optional(),
    emptyIllustrationAlt: z.string().min(1).optional(),
    emptyTitle: z.string().min(1).optional(),
    emptyDescription: z.string().min(1).optional(),
  })
  .strict();

const simplifiedCalendarRowSchema = z
  .object({
    id: z.string().min(1),
    date: z.string().min(1),
    columns: z
      .array(z.union([z.string(), z.number()]))
      .max(MAX_CALENDAR_COLUMNS),
    tooltipContent: z.string().min(1).optional(),
  })
  .strict();

const simplifiedCalendarCategorySchema = z
  .object({
    id: z.string().min(1),
    header: z.string().min(1),
    columnHeaders: z.array(z.string().min(1)).max(MAX_CALENDAR_COLUMNS),
    bgColor: z.string().min(1),
    hoverColor: z.string().min(1),
    rows: z
      .array(simplifiedCalendarRowSchema)
      .max(MAX_CALENDAR_ROWS_PER_CATEGORY),
    displayLimit: z.number().int().positive().optional(),
  })
  .strict();

export const simplifiedCalendarBlockPropsSchema = z
  .object({
    categories: z
      .array(simplifiedCalendarCategorySchema)
      .max(MAX_CALENDAR_CATEGORIES),
    languageSetting: z.string().min(1).optional(),
    displayLimit: z.number().int().positive().optional(),
    locales: simplifiedCalendarBlockLocalesSchema.optional(),
  })
  .strict();

export const cardBlockPropsSchema = cardPropsSchema;
export const typographyBlockPropsSchema = typographyPropsSchema;
export const badgeBlockPropsSchema = badgePropsSchema;

export const tableBlockColumnSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().min(1),
    alignment: z.enum(['left', 'right', 'center']).optional(),
  })
  .strict();

export const tableBlockPropsSchema = z
  .object({
    columns: z.array(tableBlockColumnSchema).min(1).max(MAX_TABLE_COLUMNS),
    rows: z
      .array(z.record(z.string(), z.union([z.string(), z.number()])))
      .max(MAX_TABLE_ROWS),
    withBodyRowHover: z.boolean().optional(),
    isHeaderSticky: z.boolean().optional(),
  })
  .strict();

const resizablePanelBlockSchema = z.lazy(() =>
  z
    .object({
      defaultSize: z.string().optional(),
      minSize: z.string().optional(),
      maxSize: z.string().optional(),
      collapsible: z.boolean().optional(),
      collapsedSize: z.string().optional(),
      content: z.array(generativeUiBlockSchema).max(MAX_BLOCKS_PER_LEVEL),
    })
    .strict()
);

export const resizableBlockPropsSchema = z
  .object({
    orientation: z.enum(['horizontal', 'vertical']),
    panels: z.array(resizablePanelBlockSchema).min(2).max(4),
    withHandle: z.boolean().optional(),
    disabled: z.boolean().optional(),
    height: z.number().positive().max(800).optional(),
  })
  .strict();

export const stackBlockPropsSchema = z
  .object({
    gap: z.enum(['sm', 'md', 'lg']).optional(),
  })
  .strict();

export const rowBlockPropsSchema = z
  .object({
    gap: z.enum(['sm', 'md', 'lg']).optional(),
    align: z.enum(['start', 'center', 'stretch']).optional(),
  })
  .strict();

export const gridBlockPropsSchema = gridPropsSchema;
export const gridRowBlockPropsSchema = gridRowPropsSchema;
export const carouselBlockPropsSchema = carouselPropsSchema;

export const watchlistTableDataProviderBlockPropsSchema = z
  .object({
    watchlistId: z.string().min(1),
    pageSize: z.number().int().positive().max(100).optional(),
    activeTab: z.string().min(1).optional(),
    stickyHeader: z.boolean().optional(),
  })
  .strict();

export const screenerTableDataProviderBlockPropsSchema = z
  .object({
    screenId: z.string().min(1),
    pageSize: z.number().int().positive().max(100).optional(),
    activeTab: z.string().min(1).optional(),
    stickyHeader: z.boolean().optional(),
  })
  .strict();

export type ChartBlockProps = z.infer<typeof chartBlockPropsSchema>;
export type ChartV2DataProviderBlockProps = z.infer<
  typeof chartV2DataProviderBlockPropsSchema
>;
export type MarkdownBlockProps = z.infer<typeof markdownBlockPropsSchema>;
export type TimeSeriesInfoBlockProps = z.infer<
  typeof timeSeriesInfoBlockPropsSchema
>;
export type PriceDisplayBlockProps = z.infer<
  typeof priceDisplayBlockPropsSchema
>;
export type AlertBlockProps = z.infer<typeof alertBlockPropsSchema>;
export type GradientCardBlockProps = z.infer<
  typeof gradientCardBlockPropsSchema
>;
export type KeyStatsBlockProps = z.infer<typeof keyStatsBlockPropsSchema>;
export type OrderedListBlockProps = z.infer<typeof orderedListBlockPropsSchema>;
export type AssetOverviewHeaderBlockProps = z.infer<
  typeof assetOverviewHeaderBlockPropsSchema
>;
export type SimplifiedCalendarBlockLocales = z.infer<
  typeof simplifiedCalendarBlockLocalesSchema
>;
export type SimplifiedCalendarBlockProps = z.infer<
  typeof simplifiedCalendarBlockPropsSchema
>;
export type CardBlockProps = z.infer<typeof cardBlockPropsSchema>;
export type TypographyBlockProps = z.infer<typeof typographyBlockPropsSchema>;
export type BadgeBlockProps = z.infer<typeof badgeBlockPropsSchema>;
export type TableBlockColumn = z.infer<typeof tableBlockColumnSchema>;
export type TableBlockProps = z.infer<typeof tableBlockPropsSchema>;
export type ResizableBlockProps = z.infer<typeof resizableBlockPropsSchema>;
export type StackBlockProps = z.infer<typeof stackBlockPropsSchema>;
export type RowBlockProps = z.infer<typeof rowBlockPropsSchema>;
export type GridBlockProps = z.infer<typeof gridBlockPropsSchema>;
export type GridRowBlockProps = z.infer<typeof gridRowBlockPropsSchema>;
export type CarouselBlockProps = z.infer<typeof carouselBlockPropsSchema>;
export type WatchlistTableDataProviderBlockProps = z.infer<
  typeof watchlistTableDataProviderBlockPropsSchema
>;
export type ScreenerTableDataProviderBlockProps = z.infer<
  typeof screenerTableDataProviderBlockPropsSchema
>;

export interface AccordionBlockItem {
  title: string;
  content?: GenerativeUiBlock[];
  expandedByDefault?: boolean;
}

export interface AccordionBlockProps {
  items: AccordionBlockItem[];
  collapseAccordionsOnClick?: boolean;
  withBackground?: boolean;
  variant?: 'primary' | 'secondary';
  disabledCollapse?: boolean;
}

export type GenerativeUiBlock =
  | { component: 'Markdown'; props: MarkdownBlockProps }
  | { component: 'Chart'; props: ChartBlockProps }
  | { component: 'ChartV2DataProvider'; props: ChartV2DataProviderBlockProps }
  | { component: 'TimeSeriesInfo'; props: TimeSeriesInfoBlockProps }
  | { component: 'AssetOverviewHeader'; props: AssetOverviewHeaderBlockProps }
  | { component: 'PriceDisplay'; props: PriceDisplayBlockProps }
  | { component: 'Alert'; props: AlertBlockProps }
  | { component: 'GradientCard'; props: GradientCardBlockProps }
  | { component: 'KeyStats'; props: KeyStatsBlockProps }
  | { component: 'OrderedList'; props: OrderedListBlockProps }
  | { component: 'SimplifiedCalendar'; props?: SimplifiedCalendarBlockProps }
  | {
      component: 'Card';
      props?: CardBlockProps;
      children?: GenerativeUiBlock[];
    }
  | { component: 'Badge'; props: BadgeBlockProps }
  | { component: 'Accordion'; props: AccordionBlockProps }
  | { component: 'Table'; props: TableBlockProps }
  | { component: 'Resizable'; props: ResizableBlockProps }
  | {
      component: 'Stack';
      props?: StackBlockProps;
      children?: GenerativeUiBlock[];
    }
  | { component: 'Row'; props?: RowBlockProps; children?: GenerativeUiBlock[] }
  | {
      component: 'Grid';
      props?: GridBlockProps;
      children?: GenerativeUiBlock[];
    }
  | {
      component: 'GridRow';
      props?: GridRowBlockProps;
      children?: GenerativeUiBlock[];
    }
  | { component: 'Separator' }
  | { component: 'Typography'; props: TypographyBlockProps }
  | {
      component: 'Carousel';
      props?: CarouselBlockProps;
      children?: GenerativeUiBlock[];
    }
  | {
      component: 'WatchlistTableDataProvider';
      props: WatchlistTableDataProviderBlockProps;
    }
  | {
      component: 'ScreenerTableDataProvider';
      props: ScreenerTableDataProviderBlockProps;
    };

const accordionBlockItemSchema: z.ZodType<AccordionBlockItem> = z.lazy(() =>
  z
    .object({
      title: z.string().min(1),
      content: z
        .array(generativeUiBlockSchema)
        .max(MAX_BLOCKS_PER_LEVEL)
        .optional(),
      expandedByDefault: z.boolean().optional(),
    })
    .strict()
);

export const accordionBlockPropsSchema: z.ZodType<AccordionBlockProps> = z
  .object({
    items: z.array(accordionBlockItemSchema).min(1).max(MAX_BLOCKS_PER_LEVEL),
    collapseAccordionsOnClick: z.boolean().optional(),
    withBackground: z.boolean().optional(),
    variant: z.enum(['primary', 'secondary']).optional(),
    disabledCollapse: z.boolean().optional(),
  })
  .strict();

export interface GenerativeUiBlockPropsMap {
  Markdown: MarkdownBlockProps;
  Chart: ChartBlockProps;
  ChartV2DataProvider: ChartV2DataProviderBlockProps;
  TimeSeriesInfo: TimeSeriesInfoBlockProps;
  AssetOverviewHeader: AssetOverviewHeaderBlockProps;
  PriceDisplay: PriceDisplayBlockProps;
  Alert: AlertBlockProps;
  GradientCard: GradientCardBlockProps;
  KeyStats: KeyStatsBlockProps;
  OrderedList: OrderedListBlockProps;
  SimplifiedCalendar: SimplifiedCalendarBlockProps;
  Card: CardBlockProps & { children?: ReactNode };
  Badge: BadgeBlockProps;
  Accordion: AccordionBlockProps;
  Table: TableBlockProps;
  Resizable: ResizableBlockProps;
  Stack: StackBlockProps & { children?: ReactNode };
  Row: RowBlockProps & { children?: ReactNode };
  Grid: GridBlockProps & { children?: ReactNode };
  GridRow: GridRowBlockProps & { children?: ReactNode };
  Separator: Record<string, never>;
  Typography: TypographyBlockProps;
  Carousel: CarouselBlockProps & { children?: ReactNode };
  WatchlistTableDataProvider: WatchlistTableDataProviderBlockProps;
  ScreenerTableDataProvider: ScreenerTableDataProviderBlockProps;
}

export interface GenerativeUiResponse {
  blocks: GenerativeUiBlock[];
}

export const generativeUiBlockSchema: z.ZodType<GenerativeUiBlock> = z.lazy(
  () =>
    z.discriminatedUnion('component', [
      z
        .object({
          component: z.literal('Markdown'),
          props: markdownBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Chart'),
          props: chartBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('ChartV2DataProvider'),
          props: chartV2DataProviderBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('TimeSeriesInfo'),
          props: timeSeriesInfoBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('AssetOverviewHeader'),
          props: assetOverviewHeaderBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('PriceDisplay'),
          props: priceDisplayBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Alert'),
          props: alertBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('GradientCard'),
          props: gradientCardBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('KeyStats'),
          props: keyStatsBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('OrderedList'),
          props: orderedListBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('SimplifiedCalendar'),
          props: simplifiedCalendarBlockPropsSchema.optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Card'),
          props: cardBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Badge'),
          props: badgeBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Accordion'),
          props: accordionBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Table'),
          props: tableBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Resizable'),
          props: resizableBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Stack'),
          props: stackBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Row'),
          props: rowBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Grid'),
          props: gridBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('GridRow'),
          props: gridRowBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Separator'),
        })
        .strict(),
      z
        .object({
          component: z.literal('Typography'),
          props: typographyBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Carousel'),
          props: carouselBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('WatchlistTableDataProvider'),
          props: watchlistTableDataProviderBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('ScreenerTableDataProvider'),
          props: screenerTableDataProviderBlockPropsSchema,
        })
        .strict(),
    ])
);

export const generativeUiResponseSchema: z.ZodType<GenerativeUiResponse> =
  z.object({
    blocks: z.array(generativeUiBlockSchema).max(MAX_BLOCKS_PER_LEVEL),
  });
